import type { IPositionMeasurement } from '../models/geolocation'
import type {
  TGeoLatLng,
  TPlaylistAssignmentGeoFilter
} from '../models/playlist-assignment-geo-filter'
import { getPlayerPositionCoordinates } from '../models/geolocation'
import {
  DEFAULT_PLAYER_POSITION_STALE_MS,
  isPlayerPositionMeasurementStale
} from './playerPositionStaleness'
import {
  geoFilterCircles,
  maxGeoFilterRadiusMeters
} from '../models/playlist-assignment-geo-filter'

const EARTH_RADIUS_M = 6_371_000

function toRadians(degrees: number): number {
  return (degrees * Math.PI) / 180
}

/** Great-circle distance in meters (haversine). */
export function haversineDistanceMeters(a: TGeoLatLng, b: TGeoLatLng): number {
  const dLat = toRadians(b.latitude - a.latitude)
  const dLng = toRadians(b.longitude - a.longitude)
  const lat1 = toRadians(a.latitude)
  const lat2 = toRadians(b.latitude)
  const h = Math.sin(dLat / 2) ** 2
    + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)))
}

function venuePoint(input: {
  venueLatitude?: number | null | undefined
  venueLongitude?: number | null | undefined
}): TGeoLatLng | null {
  const venueLat = input.venueLatitude ?? null
  const venueLng = input.venueLongitude ?? null
  if (
    venueLat != null
    && venueLng != null
    && Number.isFinite(venueLat)
    && Number.isFinite(venueLng)
    && venueLat >= -90
    && venueLat <= 90
    && venueLng >= -180
    && venueLng <= 180
  ) {
    return { latitude: venueLat, longitude: venueLng }
  }
  return null
}

/**
 * True when reported GPS accuracy is too coarse for the given radius ceiling
 * (accuracy larger than the largest geofilter circle).
 */
export function isGpsAccuracyTooCoarseForRadius(
  accuracyMeters: number | null | undefined,
  maxRadiusMeters: number | null | undefined
): boolean {
  if (maxRadiusMeters == null || !Number.isFinite(maxRadiusMeters) || maxRadiusMeters <= 0) {
    return false
  }
  if (accuracyMeters == null || !Number.isFinite(accuracyMeters) || accuracyMeters < 0) {
    return false
  }
  return accuracyMeters > maxRadiusMeters
}

/**
 * Resolve the point used for assignment geofilter matching:
 * player GPS first (unless accuracy is coarser than {@link maxGpsAccuracyMeters}), then venue.
 * Missing both → `null` (fail closed when a filter is set).
 */
export function resolvePlayerAssignmentGeoPoint(input: {
  playerPosition?: IPositionMeasurement | null | undefined
  venueLatitude?: number | null | undefined
  venueLongitude?: number | null | undefined
  /**
   * When set (typically {@link maxGeoFilterRadiusMeters}), ignore GPS if
   * `playerPosition.accuracy` exceeds this value and fall back to venue.
   */
  maxGpsAccuracyMeters?: number | null | undefined
  /** Wall clock for stale-fix checks (tests). */
  nowMs?: number
  /** Max GPS fix age before venue/fallback is preferred (defaults to map lens). */
  maxPositionAgeMs?: number
}): TGeoLatLng | null {
  const fromGps = getPlayerPositionCoordinates(input.playerPosition)
  const gpsOk = fromGps.latitude != null && fromGps.longitude != null
  const gpsTooCoarse = gpsOk
    && isGpsAccuracyTooCoarseForRadius(
      input.playerPosition?.accuracy,
      input.maxGpsAccuracyMeters
    )
  const gpsStale = gpsOk
    && isPlayerPositionMeasurementStale(
      input.playerPosition,
      input.nowMs,
      input.maxPositionAgeMs ?? DEFAULT_PLAYER_POSITION_STALE_MS
    )
  if (gpsOk && !gpsTooCoarse && !gpsStale) {
    return { latitude: fromGps.latitude!, longitude: fromGps.longitude! }
  }
  return venuePoint(input)
}

/**
 * Resolve using a specific filter's max radius for the GPS accuracy gate.
 */
export function resolvePlayerAssignmentGeoPointForFilter(input: {
  playerPosition?: IPositionMeasurement | null | undefined
  venueLatitude?: number | null | undefined
  venueLongitude?: number | null | undefined
  geoFilter?: TPlaylistAssignmentGeoFilter | null | undefined
}): TGeoLatLng | null {
  return resolvePlayerAssignmentGeoPoint({
    playerPosition: input.playerPosition,
    venueLatitude: input.venueLatitude,
    venueLongitude: input.venueLongitude,
    maxGpsAccuracyMeters: maxGeoFilterRadiusMeters(input.geoFilter)
  })
}

/**
 * True when `point` is inside **any** filter circle (inclusive of the radius edge).
 * Null / undefined filter → always eligible.
 */
export function isPointInsideGeoFilter(
  point: TGeoLatLng | null | undefined,
  filter: TPlaylistAssignmentGeoFilter | null | undefined
): boolean {
  if (filter == null) {
    return true
  }
  if (point == null) {
    return false
  }
  const circles = geoFilterCircles(filter)
  if (circles.length === 0) {
    return true
  }
  return circles.some(c => haversineDistanceMeters(point, c.center) <= c.radiusMeters)
}

/**
 * Whether a playlist assignment's optional geofilter is satisfied.
 * No filter → true. Filter set + no point → false (fail closed).
 */
export function isPlaylistAssignmentGeoSatisfied(
  geoFilter: TPlaylistAssignmentGeoFilter | null | undefined,
  geoPoint: TGeoLatLng | null | undefined
): boolean {
  return isPointInsideGeoFilter(geoPoint, geoFilter)
}

/**
 * Effective playback geo point: live GPS/venue resolve (accuracy-aware), else stamped venue fallback.
 */
export function resolvePlaybackAssignmentGeoPoint(input: {
  playerPosition?: IPositionMeasurement | null | undefined
  venueLatitude?: number | null | undefined
  venueLongitude?: number | null | undefined
  geoFallbackPoint?: TGeoLatLng | null | undefined
  maxGpsAccuracyMeters?: number | null | undefined
  nowMs?: number
  maxPositionAgeMs?: number
}): TGeoLatLng | null {
  const live = resolvePlayerAssignmentGeoPoint({
    playerPosition: input.playerPosition,
    venueLatitude: input.venueLatitude,
    venueLongitude: input.venueLongitude,
    maxGpsAccuracyMeters: input.maxGpsAccuracyMeters,
    nowMs: input.nowMs,
    maxPositionAgeMs: input.maxPositionAgeMs
  })
  if (live != null) {
    return live
  }
  const fallback = input.geoFallbackPoint
  if (
    fallback != null
    && Number.isFinite(fallback.latitude)
    && Number.isFinite(fallback.longitude)
  ) {
    return fallback
  }
  return null
}

export { maxGeoFilterRadiusMeters, geoFilterCircles }
